import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchVariantsList } from '@/api/my-styles';
import type { VariantsListParams } from '@/api/my-styles/types';
import { useNetworkCompanyMap } from '@/hooks/useDropdowns';
import { buildCodesFilter } from '../-utils/codesFilter';

export interface VariantsQueryInput {
  page: number;
  limit: number;
  isArchived?: boolean;
  /** When `true`, filter to component-variant entities (My Components route);
   *  when false / undefined, returns style variants. */
  isComponent?: boolean;
  sort?: string;
  filters?: Record<string, string[]>;
}

function parseSort(sort?: string): { sort: string; order: 'ASC' | 'DESC' } {
  if (!sort) return { sort: 'name', order: 'ASC' };
  const [field, dir] = sort.split(':');
  if (!field) return { sort: 'name', order: 'ASC' };
  return { sort: field, order: dir === 'DESC' ? 'DESC' : 'ASC' };
}

function nonEmpty(arr?: string[]): string[] | undefined {
  return arr && arr.length > 0 ? arr : undefined;
}

function buildApiParams(input: VariantsQueryInput): VariantsListParams {
  const parsed = parseSort(input.sort);
  const f = input.filters ?? {};

  const suppliers = nonEmpty(f.suppliers);
  const vendors = nonEmpty(f.vendors);
  const brands = nonEmpty(f.brands);
  const departments = nonEmpty(f.departments);
  const seasons = nonEmpty(f.seasons);
  const collections = nonEmpty(f.collections);

  const codes = buildCodesFilter(f);
  const isStoryActiveRaw = f.isStoryActive?.[0];
  const isStoryActive =
    isStoryActiveRaw === 'true'
      ? true
      : isStoryActiveRaw === 'false'
        ? false
        : undefined;

  return {
    page: input.page,
    limit: input.limit,
    include: ['type', 'styleProperties', 'material', 'tracingStory', 'style'],
    isArchived: input.isArchived ?? false,
    isComponent: input.isComponent ?? false,
    sort: parsed.sort,
    order: parsed.order,
    ...(f.name?.[0] !== undefined ? { name: f.name[0] } : {}),
    ...(f.color?.[0] !== undefined ? { color: f.color[0] } : {}),
    ...(f.styleId?.[0] !== undefined ? { styleId: f.styleId[0] } : {}),
    ...(f.styleTypeId?.[0] !== undefined
      ? { styleTypeId: f.styleTypeId[0] }
      : {}),
    ...(f.mainSupplier?.[0] !== undefined
      ? { mainSupplier: f.mainSupplier[0] }
      : {}),
    ...(suppliers !== undefined ? { suppliers } : {}),
    ...(vendors !== undefined ? { vendors } : {}),
    ...(brands !== undefined ? { brands } : {}),
    ...(departments !== undefined ? { departments } : {}),
    ...(seasons !== undefined ? { seasons } : {}),
    ...(collections !== undefined ? { collections } : {}),
    ...(codes !== undefined ? { codes } : {}),
    ...(isStoryActive !== undefined ? { isStoryActive } : {}),
  };
}

export function variantsQueryOptions(input: VariantsQueryInput) {
  const apiParams = buildApiParams(input);
  return queryOptions({
    queryKey: ['my-variants', 'list', apiParams] as const,
    queryFn: () => fetchVariantsList(apiParams),
  });
}

export function useVariantsQuery(input: VariantsQueryInput) {
  const query = useQuery(variantsQueryOptions(input));
  const companyMap = useNetworkCompanyMap();
  return { query, companyMap };
}
